/**
 * Moving to the system's own address.
 *
 * The server says where it lives (PUBLIC_URL). A page opened at any other
 * address - an old one the system has since moved from - goes there.
 *
 * But NOT while this device still has sales waiting to upload. Those sales sit
 * in this address's own storage on the device, and the browser keeps each
 * address's storage separate: at the new address they would be invisible, never
 * uploaded, and their receipt numbers would be handed out again. So the move
 * waits until the outbox is empty, and the till says why it is waiting.
 *
 * The shop's account code travels with the move, so the sign-in screen at the
 * new address already knows which shop it is.
 */

export interface MoveState {
  /** The address the system now lives at, e.g. pos.appbay.dev. */
  host: string;
  /** Sales still to upload before the move can happen. */
  waiting: number;
}

async function publicUrl(): Promise<URL | null> {
  try {
    const res = await fetch('/api/health', { cache: 'no-store' });
    const body = (await res.json()) as { publicUrl?: unknown };
    return typeof body.publicUrl === 'string' && body.publicUrl ? new URL(body.publicUrl) : null;
  } catch {
    // Offline, or no address set: stay where we are.
    return null;
  }
}

/**
 * Move now if nothing is waiting, otherwise report what is holding the move up.
 * Null when there is nothing to move (already home, offline, or not configured).
 */
export async function moveIfRehomed(pending: () => Promise<number>, accountCode?: string | null): Promise<MoveState | null> {
  const home = await publicUrl();
  if (!home || home.origin === window.location.origin) return null;
  const waiting = await pending();
  if (waiting === 0) {
    const dest = new URL(window.location.pathname + window.location.search, home.origin);
    if (accountCode && !dest.searchParams.has('account')) dest.searchParams.set('account', accountCode);
    window.location.replace(dest.toString());
  }
  return { host: home.host, waiting };
}
